import { ScheduledEventNavigation } from '../components/scheduled-event-navigation/ScheduledEventNavigation';
import { captureDeviceId, eventMeasures, verifiedBinding, versionedPayload, type CapturedMeasurement, type InstanceBinding } from '../utils/versioned-capture';
import type { ConfigVersion } from '../providers/configuration-types';
/**
 * BatchGradeGrid — E6-S2 (roster entry) + E6-S1 (offline-first).
 *
 * Pick an assessment instance + event, then score an entire platoon/company
 * with keyboard-navigable inputs and a single Save press. Writes go through
 * an IndexedDB-backed offline queue (see src/utils/offline-queue.ts) so
 * cadre can grade in airplane mode; the queue auto-drains when the browser
 * reconnects.
 *
 * E6-S2 accept: 40 trainees scored on one AFT event in under 4 minutes.
 * E6-S1 accept: score 10 trainees offline, reconnect, zero loss.
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  useDataProvider,
  useNotify,
  Title,
} from 'react-admin';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { list as listQueue, type QueueItem } from '../utils/offline-queue';
import { getSessionGeneration, subscribeSession } from '../providers/cognito';
import {
  captureQueueAccess,
  isCaptureSessionFor,
  isCaptureSessionCurrent,
  loadCaptureSession,
  submitCaptureOperation,
  type CaptureSession,
} from './capture-session';
import { apiFetch } from '../providers/api-rest-provider';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SyncIcon from '@mui/icons-material/Sync';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MeasurementDef {
  measurement_id: string;
  name: string;
  unit_scheme_id: string;
  lower_is_better?: boolean;
}

interface EventItem {
  id: string;
  name: string;
  scoring_mode: 'measured' | 'pass_fail';
  max_attempts?: number;
  measurements?: MeasurementDef[];
}

interface StudentItem {
  id: string;
  first_name: string;
  last_name: string;
  middle_initial?: string;
  [key: string]: unknown;
}

interface InstanceItem extends InstanceBinding {
  id: string;
  name?: string;
  student_group_id?: string;
  lanes?: { student_ids?: string[] }[];
  [key: string]: unknown;
}

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

interface GradeRow {
  student: StudentItem;
  // pass_fail
  verdict?: 'GO' | 'NO-GO';
  // measured — raw input string(s) per part
  inputs: string[];
  status: RowStatus;
  errorMsg?: string;
  // Stable client-side operation ID assigned when the row is enqueued.
  // Used to look up per-row sync state in the offline queue snapshot.
  client_op_id?: string;
  captured?: Record<string, { status: CapturedMeasurement['status']; value: string }>;
}

const rosterOwnerKeyFor = (session: CaptureSession): string => JSON.stringify([
  session.tenantId,
  session.actorId,
  session.instanceId,
  session.eventId,
]);

// ---------------------------------------------------------------------------
// Unit scheme helpers (client-side normalization)
// ---------------------------------------------------------------------------

interface SchemePart {
  factor: number;
  max?: number;
}

const SCHEME_PARTS: Record<string, SchemePart[]> = {
  WEIGHT_POUNDS:       [{ factor: 1 }],
  WEIGHT_KILOGRAMS:    [{ factor: 1 }],
  QUANTITY_REPS:       [{ factor: 1 }],
  QUANTITY_ROUNDS:     [{ factor: 1 }],
  SCORE_POINTS:        [{ factor: 1 }],
  DISTANCE_METERS:     [{ factor: 1 }],
  DISTANCE_FEET_INCHES:[{ factor: 12 }, { factor: 1, max: 11 }],
  DISTANCE_INCHES:     [{ factor: 1 }],
  DISTANCE_YARDS:      [{ factor: 36 }],
  TIME_MIN_SEC_TENTHS: [{ factor: 600 }, { factor: 10, max: 59 }, { factor: 1, max: 9 }],
  TIME_SEC_TENTHS:     [{ factor: 10 }, { factor: 1, max: 9 }],
};

const SCHEME_LABELS: Record<string, string[]> = {
  WEIGHT_POUNDS:       ['lbs'],
  WEIGHT_KILOGRAMS:    ['kg'],
  QUANTITY_REPS:       ['reps'],
  QUANTITY_ROUNDS:     ['rounds'],
  SCORE_POINTS:        ['pts'],
  DISTANCE_METERS:     ['m'],
  DISTANCE_FEET_INCHES:['ft', 'in'],
  DISTANCE_INCHES:     ['in'],
  DISTANCE_YARDS:      ['yds'],
  TIME_MIN_SEC_TENTHS: ['min', 'sec', 'tenths'],
  TIME_SEC_TENTHS:     ['sec', 'tenths'],
};

// Fall back for composed TIME_* schemes (e.g. TIME_MIN_SEC)
function resolveScheme(id: string): SchemePart[] | null {
  if (SCHEME_PARTS[id]) return SCHEME_PARTS[id];
  if (id.startsWith('TIME_')) {
    const codeMap: Record<string, SchemePart> = {
      HR:        { factor: 36000 },
      MIN:       { factor: 600 },
      SEC:       { factor: 10, max: 59 },
      TENTHS:    { factor: 1, max: 9 },
      HUNDREDTHS:{ factor: 1, max: 9 },
    };
    const codes = id.slice(5).split('_');
    const parts = codes.map((c) => codeMap[c]).filter(Boolean);
    return parts.length > 0 ? parts : null;
  }
  return null;
}

function resolveLabels(id: string): string[] {
  if (SCHEME_LABELS[id]) return SCHEME_LABELS[id];
  if (id.startsWith('TIME_')) {
    const labelMap: Record<string, string> = {
      HR: 'hr', MIN: 'min', SEC: 'sec', TENTHS: 'tenths', HUNDREDTHS: 'hun',
    };
    return id.slice(5).split('_').map((c) => labelMap[c] ?? c.toLowerCase());
  }
  return ['value'];
}

function normalize(inputs: string[], schemeId: string): number | null {
  const parts = resolveScheme(schemeId);
  if (!parts) return null;
  let total = 0;
  for (let i = 0; i < parts.length; i++) {
    const val = parseFloat(inputs[i] ?? '0') || 0;
    total += val * parts[i].factor;
  }
  return Math.round(total * 10) / 10;
}

function partCount(schemeId: string): number {
  return resolveScheme(schemeId)?.length ?? 1;
}

// ---------------------------------------------------------------------------
// Sub-component: input cell for one measurement
// ---------------------------------------------------------------------------

interface MeasInputProps {
  schemeId: string;
  accessibleName: string;
  statusId: string;
  inputs: string[];
  onChange: (inputs: string[]) => void;
  onAdvance: () => void;
  rowIdx: number;
  disabled: boolean;
  inputRefs: React.MutableRefObject<Map<string, HTMLInputElement>>;
}

function MeasInput({ schemeId, accessibleName, statusId, inputs, onChange, onAdvance, rowIdx, disabled, inputRefs }: MeasInputProps) {
  const labels = resolveLabels(schemeId);
  const count = partCount(schemeId);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {Array.from({ length: count }).map((_, pi) => (
        <Box key={pi} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <TextField
            size="small"
            variant="outlined"
            type="number"
            value={inputs[pi] ?? ''}
            disabled={disabled}
            inputProps={{ 'aria-label': `${accessibleName} (${labels[pi]})`, 'aria-describedby': statusId, min: 0, step: 1, style: { width: count === 1 ? 72 : 52, textAlign: 'center' } }}
            inputRef={(el) => {
              if (el) inputRefs.current.set(`${rowIdx}-${pi}`, el);
              else inputRefs.current.delete(`${rowIdx}-${pi}`);
            }}
            onChange={(e) => {
              const next = [...inputs];
              next[pi] = e.target.value;
              onChange(next);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdvance();
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
            {labels[pi]}
          </Typography>
          {pi < count - 1 && <Typography sx={{ mx: 0.25 }} color="text.secondary">:</Typography>}
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BatchGradeGrid() {
  const dp = useDataProvider();
  const notify = useNotify();

  // ---- Step 1: selectors ----
  const [instances, setInstances] = useState<InstanceItem[]>([]);
  const [binding, setBinding] = useState<{ instance: InstanceItem; config: ConfigVersion }>();
  const [receiptQuality, setReceiptQuality] = useState<string[]>([]);
  const [instanceConfigured, setInstanceConfigured] = useState(false);
  const [bindingError, setBindingError] = useState('Select an explicitly configured instance for new capture.');
  const [observedAt, setObservedAt] = useState('');
  const [historicalEvents, setHistoricalEvents] = useState<EventItem[]>([]);
  const [eventItems, setEventItems] = useState<EventItem[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loadingSelectors, setLoadingSelectors] = useState(false);

  // ---- Step 2: roster ----
  const [rows, setRows] = useState<GradeRow[]>([]);
  const [event, setEvent] = useState<EventItem | null>(null);
  const [rosterOwnerKey, setRosterOwnerKey] = useState<string | null>(null);
  const [loadingRoster, setLoadingRoster] = useState(false);

  // ---- Step 3: submit state ----
  const [submitting, setSubmitting] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [finishTime, setFinishTime] = useState<number | null>(null);

  const rosterRef = useRef<HTMLDivElement>(null);
  const focusAfterRender = useRef<number | null>(null);
  useEffect(() => {
    const index = focusAfterRender.current;
    if (index === null || rows.length === 0) return;
    focusAfterRender.current = null;
    rosterRef.current?.querySelector<HTMLElement>(`[data-capture-row="${index}"] input:not(:disabled):not([aria-hidden="true"]), [data-capture-row="${index}"] button:not(:disabled)`)?.focus();
  }, [rows]);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const sessionGeneration = useSyncExternalStore(
    subscribeSession,
    getSessionGeneration,
    getSessionGeneration,
  );

  // ---- E6-S1: offline queue ----
  // Queue ownership is established by the authenticated API and the signed
  // JWT before any IndexedDB record can be displayed, changed, or submitted.
  const [captureSession, setCaptureSession] = useState<CaptureSession | null>(null);
  const rosterRequestIdRef = useRef(0);

  useEffect(() => {
    let active = true;
    setCaptureSession(null);
    if (!selectedInstanceId || !selectedEventId) return () => { active = false; };

    void loadCaptureSession(selectedInstanceId, selectedEventId)
      .then(session => {
        if (active && isCaptureSessionCurrent(session)) setCaptureSession(session);
      })
      .catch(() => {
        if (active) setCaptureSession(null);
      });
    return () => { active = false; };
  }, [selectedInstanceId, selectedEventId, sessionGeneration]);

  const activeCaptureSession = isCaptureSessionFor(
    captureSession,
    selectedInstanceId,
    selectedEventId,
  ) ? captureSession : null;
  // PR capture-identity view, derived only from the verified, pinned capture session.
  const capture = useMemo(() => ({
    context: activeCaptureSession
      ? { tenantId: activeCaptureSession.tenantId, actorId: activeCaptureSession.actorId }
      : undefined,
    token: activeCaptureSession?.sessionToken,
    isCurrent: () => Boolean(activeCaptureSession && isCaptureSessionCurrent(activeCaptureSession)),
  }), [activeCaptureSession]);
  const activeRosterOwnerKey = activeCaptureSession
    ? rosterOwnerKeyFor(activeCaptureSession)
    : null;
  const rosterOwnedByActiveSession = Boolean(
    activeRosterOwnerKey && rosterOwnerKey === activeRosterOwnerKey,
  );
  const rosterContextRef = useRef({
    instanceId: selectedInstanceId,
    eventId: selectedEventId,
    sessionGeneration,
    captureSession: activeCaptureSession,
  });
  rosterContextRef.current = {
    instanceId: selectedInstanceId,
    eventId: selectedEventId,
    sessionGeneration,
    captureSession: activeCaptureSession,
  };

  const queueAccess = useMemo(
    () => activeCaptureSession ? captureQueueAccess(activeCaptureSession) : undefined,
    [activeCaptureSession],
  );

  interface QueuedAttempt {
    student_id: string;
    assessment_instance_id: string;
    event_id: string;
    pass?: boolean;
    measurement_results?: {
      measurement_id: string;
      raw_parts: number[];
      raw_normalized: number;
    }[];
    client_op_id: string;
    capture_schema_version?: number;
    measurements?: CapturedMeasurement[];
  }

  const submitQueuedAttempt = useCallback(async (
    payload: QueuedAttempt,
    operationId: string,
  ) => {
    if (!activeCaptureSession) throw new Error('Capture owner is not verified');
    const receipt = await submitCaptureOperation(activeCaptureSession, payload, operationId) as { id?: unknown; quality_flags?: unknown } | undefined;
    if (isCaptureSessionCurrent(activeCaptureSession) && Array.isArray(receipt?.quality_flags)) {
      setReceiptQuality(receipt.quality_flags.filter((flag: unknown): flag is string => typeof flag === 'string'));
    }
    if (typeof receipt?.id !== 'string' || !receipt.id) {
      throw new Error('Server acceptance was not acknowledged; the original operation is retained for retry');
    }
  }, [activeCaptureSession]);

  const {
    isOnline,
    pending,
    error: queueError,
    enqueue,
    flush,
    sweepSynced,
  } = useOfflineQueue<QueuedAttempt>({
    access: queueAccess,
    autoFlushOnScopeChange: false,
    worker: submitQueuedAttempt,
  });

  const pendingRef = useRef(pending); pendingRef.current = pending;

  // Look up the queue status for a given row (by client_op_id).
  const pendingByOpId = useMemo(() => {
    const m = new Map<string, QueueItem<QueuedAttempt>>();
    for (const item of pending as QueueItem<QueuedAttempt>[]) {
      if (item.payload?.client_op_id) m.set(item.payload.client_op_id, item);
    }
    return m;
  }, [pending]);

  // Load instances + events on mount
  useEffect(() => {
    setLoadingSelectors(true);
    Promise.all([
      dp.getList('instances', { pagination: { page: 1, perPage: 200 }, sort: { field: 'name', order: 'ASC' }, filter: {} }),
      dp.getList('events', { pagination: { page: 1, perPage: 200 }, sort: { field: 'name', order: 'ASC' }, filter: {} }),
    ])
      .then(([instRes, evRes]) => {
        setInstances(instRes.data as InstanceItem[]);
        setHistoricalEvents(evRes.data as EventItem[]); setEventItems(evRes.data as EventItem[]);
      })
      .catch(() => notify('Failed to load instances or events', { type: 'error' }))
      .finally(() => setLoadingSelectors(false));
  }, [dp, notify]);

  useEffect(() => {
    let cancelled = false;
    setReceiptQuality([]); setEventItems(historicalEvents);
    setBinding(undefined); setInstanceConfigured(false); setBindingError('The instance is not bound to an eligible published configuration. New capture is blocked.');
    if (!selectedInstanceId) return;
    void (async () => {
      try {
        const instance = (await dp.getOne('instances', { id: selectedInstanceId })).data as InstanceItem;
        if (!instance.configuration_id) return;
        setInstanceConfigured(true);
        const config = await apiFetch(`configurations/${encodeURIComponent(instance.configuration_id)}`) as ConfigVersion;
        if (cancelled) return;
        if (config.program_id !== instance.program_id || config.id !== instance.configuration_id || config.digest !== instance.configuration_digest) throw new Error('Configuration identity does not match the instance.');
        const configured: EventItem[] = config.definitions.filter(d => d.kind === 'event').map(d => ({ id: d.id, name: String(d.spec.name ?? d.id),
          scoring_mode: d.spec.capture_input === 'go_no_go' ? 'pass_fail' : 'measured', max_attempts: typeof d.spec.max_attempts === 'number' ? d.spec.max_attempts : undefined }));
        setEventItems([...configured, ...historicalEvents.filter(e => !configured.some(c => c.id === e.id)).map(e => ({ ...e, name: `${e.name} (historical replay only)` }))]);
        setSelectedEventId(''); setRows([]);
        verifiedBinding(instance, config); setBinding({ instance, config }); setBindingError('');
      } catch (e) { if (!cancelled) setBindingError(e instanceof Error ? e.message : 'Configuration binding could not be verified.'); }
    })();
    return () => { cancelled = true; };
  }, [selectedInstanceId, dp, historicalEvents]);

  // Build roster when both selections made
  const loadRoster = useCallback(async () => {
    if (!selectedInstanceId || !selectedEventId || !activeCaptureSession) return;
    const requestedInstanceId = selectedInstanceId;
    const requestedEventId = selectedEventId;
    const requestedGeneration = sessionGeneration;
    const requestedCaptureSession = activeCaptureSession;
    const requestedOwnerKey = rosterOwnerKeyFor(requestedCaptureSession);
    const requestId = ++rosterRequestIdRef.current;
    const isCurrentRequest = () => {
      const current = rosterContextRef.current;
      return (
        requestId === rosterRequestIdRef.current &&
        current.instanceId === requestedInstanceId &&
        current.eventId === requestedEventId &&
        current.sessionGeneration === requestedGeneration &&
        getSessionGeneration() === requestedGeneration &&
        current.captureSession === requestedCaptureSession &&
        isCaptureSessionFor(
          requestedCaptureSession,
          requestedInstanceId,
          requestedEventId,
        )
      );
    };
    setLoadingRoster(true);
    setRows([]);
    setEvent(null);
    setRosterOwnerKey(null);
    setSavedCount(0);
    setStartTime(null);
    setFinishTime(null);

    try {
      const [instRes, evRes] = await Promise.all([
        dp.getOne('instances', { id: requestedInstanceId }),
        binding?.config.definitions.some(d => d.kind === 'event' && d.id === requestedEventId) ? Promise.resolve({ data: eventItems.find(e => e.id === requestedEventId)! }) : dp.getOne('events', { id: requestedEventId }),
      ]);
      if (!isCurrentRequest()) return;
      const inst = instRes.data as InstanceItem;
      const ev = evRes.data as EventItem;
      setObservedAt(new Date().toISOString());

      // Collect student IDs from lanes or student_group_id
      let studentIds: string[] = [];
      if (inst.lanes && inst.lanes.length > 0) {
        for (const lane of inst.lanes) {
          studentIds.push(...(lane.student_ids ?? []));
        }
        studentIds = [...new Set(studentIds)];
      } else if (inst.student_group_id) {
        const sgRes = await dp.getOne('student_groups', { id: inst.student_group_id });
        if (!isCurrentRequest()) return;
        studentIds = (sgRes.data as { student_ids?: string[] }).student_ids ?? [];
      }

      if (studentIds.length === 0) {
        if (!isCurrentRequest()) return;
        notify('No students assigned to this instance. Use PUT /instances/:id/lanes to assign.', { type: 'warning' });
        return;
      }

      // Fetch student records
      const studentRecords = await Promise.all(
        studentIds.map((sid) => dp.getOne('students', { id: sid }).catch(() => null)),
      );
      if (!isCurrentRequest()) return;
      const students = studentRecords
        .filter(Boolean)
        .map((r) => r!.data as StudentItem);

      const measCount = ev.measurements?.[0] ? partCount(ev.measurements[0].unit_scheme_id) : 1;

      const existing = [...((await listQueue(captureQueueAccess(requestedCaptureSession)).catch(() => pendingRef.current)) as QueueItem<QueuedAttempt>[])];
      const latest = new Map<string, QueueItem<QueuedAttempt>>();
      for (const item of existing.sort((a, b) => a.createdAt - b.createdAt)) latest.set(item.payload.student_id, item);
      if (!isCurrentRequest()) return;
      focusAfterRender.current = 0;
      const nextRows = students.map((student) => {
        const queued = latest.get(student.id);
        return {
          student,
          verdict: queued?.payload.capture_schema_version === 2 && ev.scoring_mode === 'pass_fail' ? (queued.payload.measurements?.[0]?.raw_value === 1 ? 'GO' as const : queued.payload.measurements?.[0]?.raw_value === 0 ? 'NO-GO' as const : undefined) : queued?.payload.pass === undefined ? undefined : queued.payload.pass ? 'GO' as const : 'NO-GO' as const,
          captured: Object.fromEntries((queued?.payload.measurements ?? []).map(m => [m.measurement_id, { status: m.status, value: m.raw_value === null ? '' : String(m.raw_value) }])),
          inputs: queued?.payload.measurement_results?.[0]?.raw_parts.map(String) ?? Array(measCount).fill(''),
          status: queued?.status === 'synced' ? 'saved' as const :
            queued && ['error', 'conflict', 'authentication', 'blocked'].includes(queued.status) ? 'error' as const :
            queued ? 'saving' as const : 'idle' as const,
          client_op_id: queued?.payload.client_op_id,
          errorMsg: queued?.errorMessage,
        };
      });
      setRosterOwnerKey(requestedOwnerKey);
      setEvent(ev);
      setRows(nextRows);
    } catch {
      if (isCurrentRequest()) notify('Error loading roster', { type: 'error' });
    } finally {
      if (requestId === rosterRequestIdRef.current) setLoadingRoster(false);
    }
  }, [selectedInstanceId, selectedEventId, sessionGeneration, activeCaptureSession, dp, notify, binding, eventItems]);

  // Focus the first input in a row
  const focusRow = useCallback((idx: number) => {
    const el = inputRefs.current.get(`${idx}-0`);
    el?.focus();
    el?.select();
  }, []);

  const advanceFrom = useCallback(
    (idx: number) => {
      for (let next = idx + 1; next < rows.length; next++) {
        const input = inputRefs.current.get(`${next}-0`);
        if (input && !input.disabled) { focusRow(next); return; }
      }
      saveButtonRef.current?.focus();
    },
    [rows.length, focusRow],
  );

  // Submit all rows — enqueue to the offline queue, then let it drain.
  // E6-S1: this path works identically online and offline. When offline,
  // enqueue succeeds locally; flush() will no-op until reconnect and then
  // the useOfflineQueue reconnect-effect fires automatically.
  const handleSubmit = useCallback(async () => {
    if (!event || !selectedInstanceId) return;
    if (!activeCaptureSession || !rosterOwnedByActiveSession) {
      notify('Capture owner could not be verified. Sign in again before saving.', { type: 'error' });
      return;
    }
    const legacyCapture = !binding && !instanceConfigured;
    if (!legacyCapture && (!binding || !binding.config.definitions.some(d => d.kind === 'event' && d.id === selectedEventId))) { notify(bindingError || 'Historical event: replay existing operations only.', { type: 'error' }); return; }
    setSubmitting(true);
    setSavedCount(0);
    setStartTime(Date.now());
    setFinishTime(null);

    const definitions = binding ? eventMeasures(binding.config, selectedEventId).measures : [];
    const meas = event.measurements?.[0];
    let enqueuedNow = 0;

    // Two passes: (1) enqueue everything, (2) flush. This keeps the
    // enqueue-side fast (durable via IndexedDB) and separates local
    // durability from network success.
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.client_op_id && pendingByOpId.has(row.client_op_id)) continue; // Existing operations are immutable; flush retries them.
      if (binding && !navigator.onLine) {
        notify('New configuration-bound capture requires an online session until verified offline grants are available. Existing operations remain unchanged.', { type: 'error' });
        break;
      }
      // Skip rows with no data entered
      if (legacyCapture) {
        if (event.scoring_mode === 'pass_fail' && !row.verdict) continue;
        if (event.scoring_mode === 'measured' && row.inputs.every((v) => v === '' || v === '0')) continue;
      } else if (event.scoring_mode === 'pass_fail' && !row.verdict && !definitions.some(m => row.captured?.[m.id] && row.captured[m.id].status !== 'observed')) continue;
      else if (event.scoring_mode === 'measured' && !definitions.some(m => row.captured?.[m.id] && (row.captured[m.id].value !== '' || row.captured[m.id].status !== 'observed'))) continue;

      // Stable client_op_id per (row, current inputs) so a re-Save doesn't
      // create a duplicate attempt if the previous enqueue is still in the
      // queue. The client_op_id lives on the row and follows it through
      // any subsequent flush.
      const client_op_id = row.client_op_id ?? crypto.randomUUID();

      let payload: QueuedAttempt;
      if (legacyCapture) {
        payload = { student_id: row.student.id, assessment_instance_id: selectedInstanceId, event_id: selectedEventId, client_op_id };
        if (event.scoring_mode === 'pass_fail') payload.pass = row.verdict === 'GO';
        else if (meas) payload.measurement_results = [{ measurement_id: meas.measurement_id, raw_parts: row.inputs.map((v) => parseFloat(v) || 0), raw_normalized: normalize(row.inputs, meas.unit_scheme_id) ?? 0 }];
      } else try {
        const measurements: CapturedMeasurement[] = definitions.map(m => {
          const input = row.captured?.[m.id];
          if (event.scoring_mode === 'pass_fail') {
            const status = input?.status ?? 'observed';
            if (status === 'observed' && !row.verdict) throw new Error('Choose GO or NO-GO, or an explicit non-observed state.');
            return { measurement_id: m.id, status, raw_value: status === 'observed' ? row.verdict === 'GO' ? 1 : 0 : null, unit_id: m.unitId };
          }
          if (!input || (input.status === 'observed' && input.value.trim() === '')) throw new Error(`${m.label}: enter a value or choose an explicit missing/not attempted/invalid state.`);
          return { measurement_id: m.id, status: input.status, raw_value: input.status === 'observed' ? Number(input.value) : null, unit_id: m.unitId };
        });
        payload = versionedPayload(binding!.instance, binding!.config, selectedEventId, row.student.id, client_op_id, measurements, captureDeviceId(), observedAt);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Invalid capture';
        setRows(prev => prev.map((r, index) => index === i ? { ...r, status: 'error', errorMsg: message } : r));
        continue;
      }

      setRows((prev) => {
        const next = [...prev];
        next[i] = { ...next[i], status: 'saving', client_op_id };
        return next;
      });
      try {
        await enqueue(payload, client_op_id);
        enqueuedNow++;
      } catch (err) {
        const msg = typeof (err as Error)?.message === 'string' ? (err as Error).message : 'Enqueue failed';
        notify(`Could not save locally: ${msg}`, { type: 'error' });
        setRows((prev) => {
          const next = [...prev];
          next[i] = { ...next[i], status: 'error', errorMsg: msg };
          return next;
        });
      }
    }

    // Try to sync now if online. If offline, this is a no-op and the
    // useOfflineQueue reconnect-effect will drain later.
    if (isOnline) {
      let result;
      try { result = await flush(); }
      catch (err) {
        notify(err instanceof Error ? err.message : 'Local queue synchronization failed', { type: 'error' });
        setSubmitting(false);
        return;
      }
      setSavedCount(result.synced);
      setFinishTime(Date.now());
      if (result.synced > 0) {
        notify(`${result.synced} attempt${result.synced !== 1 ? 's' : ''} synced`, { type: 'success' });
      }
      if (result.errored > 0) {
        notify(`${result.errored} attempt${result.errored !== 1 ? 's' : ''} not accepted — retryable failures are retained; conflicts require review`, { type: 'warning' });
      }
    } else {
      setFinishTime(Date.now());
      notify(
        `${enqueuedNow} attempt${enqueuedNow !== 1 ? 's' : ''} queued offline — will sync automatically when the browser reconnects`,
        { type: 'info' },
      );
    }
    setSubmitting(false);
  }, [
    rows,
    event,
    selectedInstanceId,
    selectedEventId,
    activeCaptureSession,
    rosterOwnedByActiveSession,
    enqueue,
    flush,
    isOnline,
    notify,
    pendingByOpId,
    instanceConfigured,
    binding,
    bindingError,
    observedAt,
  ]);

  // When queue items land in status=synced, reflect that on the row.
  // Also expose queue-derived counts for the footer readout.
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        if (!r.client_op_id) return r;
        const q = pendingByOpId.get(r.client_op_id);
        if (!q) return r;
        // Map queue status → row status only when it advances the row.
        if (q.status === 'synced' && r.status !== 'saved') {
          changed = true;
          return { ...r, status: 'saved' as const };
        }
        if (['error', 'conflict', 'authentication', 'blocked'].includes(q.status) && r.status !== 'error') {
          changed = true;
          return { ...r, status: 'error' as const, errorMsg: q.errorMessage ?? 'Sync failed' };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [pendingByOpId]);

  const elapsedSec = finishTime && startTime ? ((finishTime - startTime) / 1000).toFixed(1) : null;
  const schemeId = event?.measurements?.[0]?.unit_scheme_id ?? '';
  const configuredEvent = !!binding && binding.config.definitions.some(d => d.id === selectedEventId && d.kind === 'event');
  const boundTeam = !!binding && binding.config.definitions.some(d => d.id === selectedEventId && d.kind === 'event' && d.spec.participation === 'team');
  const boundMeasures = binding && configuredEvent ? eventMeasures(binding.config, selectedEventId).measures : [];

  return (
    <Box sx={{
      '& button': { minHeight: 44, minWidth: 44 },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#6C757D' },
      '& .MuiInputBase-root': { minHeight: 44 },
      '& :focus-visible': { outline: '3px solid #005FCC', outlineOffset: '3px' },
    }}>
      <Title title="Batch Grade — Roster Entry" />
      {binding && capture.context && capture.token && <ScheduledEventNavigation pin={{tenantId:capture.context.tenantId,actorId:capture.context.actorId,instanceId:selectedInstanceId,programId:binding.config.program_id,configuration:{id:binding.config.id,revision:binding.instance.configuration_revision!,digest:binding.config.digest!}}} token={capture.token} isCurrent={capture.isCurrent} selectedEventId={selectedEventId} form="individual" draftKey={JSON.stringify([rows,observedAt])} disabled={submitting||loadingRoster} onSelect={id=>{setSelectedEventId(id);setRows([]);setObservedAt('');setSavedCount(0);setStartTime(null);setFinishTime(null);}} /> }
      {binding && selectedEventId && !configuredEvent && <Alert severity="info">Historical event: only retained operations can replay; new capture is disabled.</Alert>}
      {boundTeam && <Alert severity="warning">This is a team event. Use Configured Team Capture from the menu to confirm the roster and submit its complete operation. Individual submissions remain blocked.</Alert>}
      {receiptQuality.length > 0 && <Alert severity="warning">Latest server receipt quality flags: {receiptQuality.join(', ')}. Review Capture quality and corrections.</Alert>}
      {!isOnline && <Alert severity="warning">New configuration-bound capture is blocked offline until verified offline grants are available. Retained operations remain unchanged for reconciliation.</Alert>}
      {bindingError && <Alert severity="warning">{bindingError} Existing queued operations retain their original schema and can still reconcile.</Alert>}
      {binding && <Alert severity="info" role="note">Schema 2: program {binding.config.program_id}, configuration {binding.config.id}, published revision {binding.instance.configuration_revision}, digest {binding.instance.configuration_digest}. Offline first acceptance is not guaranteed after configuration expiry or retirement.</Alert>}
      {binding && <TextField label="Reported observation time (UTC ISO)" value={observedAt} onChange={e => setObservedAt(e.target.value)} helperText="Client-reported time and device identity are not attested. Existing queued operations keep their original provenance." />}
      {pending.some(p => p.status !== 'synced') && <Button onClick={() => void flush().catch(e => notify(String(e), { type: 'error' }))}>Retry retained operations unchanged</Button>}
      <Typography role="status" aria-live="polite" aria-atomic="true" variant="body2" sx={{ mb: 1 }}>
        {isOnline ? 'Online.' : 'Offline.'} {rows.length} trainees loaded. {pending.filter(p => p.status === 'synced').length} accepted by server; {pending.filter(p => p.status !== 'synced').length} retained locally awaiting acceptance.
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SpeedIcon color="primary" />
            <Typography component="h1" variant="h6">Roster-Speed Grade Entry</Typography>
            <Chip label="E6-S2" size="small" color="warning" />
            <Chip
              label={isOnline ? 'online' : 'offline'}
              size="small"
              color={isOnline ? 'success' : 'warning'}
              icon={isOnline ? <CloudDoneIcon /> : <CloudOffIcon />}
            />
            {pending.length > 0 && (
              <Chip
                label={`${pending.filter((p) => p.status === 'queued' || p.status === 'syncing').length} pending / ${pending.filter((p) => ['error', 'conflict', 'authentication', 'blocked'].includes(p.status)).length} errors`}
                size="small"
                color={pending.some((p) => ['error', 'conflict', 'authentication', 'blocked'].includes(p.status)) ? 'error' : 'default'}
                icon={<CloudQueueIcon />}
              />
            )}
          </Box>
          {!isOnline && (
            <Alert
              severity="warning"
              icon={<CloudOffIcon />}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => void sweepSynced()}>
                  Clear synced
                </Button>
              }
            >
              Browser is offline. New grading is blocked until you reconnect. Only already-retained operations can recover
              and retry when the connection returns while this session remains valid. Server acceptance is shown separately; keep this browser data until acceptance is confirmed.
            </Alert>
          )}
          {queueError && <Alert severity="error">Local storage: {queueError}. Save is not confirmed until the local queue succeeds.</Alert>}
          {pending.some(p => p.status === 'authentication') && <Alert severity="warning">Sign in again as the original instructor to resume synchronization.</Alert>}
          {pending.some(p => p.status === 'blocked') && <Alert severity="error">A retained operation requires review before it can be accepted.</Alert>}
          {pending.some(p => p.status === 'conflict') && <Alert severity="error" sx={{ mb: 2 }}>
            A conflicting operation is retained locally and needs reconciliation. Do not clear browser storage or re-enter it as a new attempt.
          </Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an assessment instance and event, then score the entire platoon with
            keyboard navigation (Tab and Shift+Tab between controls; Enter advances to the next editable row or Save All). Press{' '}
            <strong>Save All</strong> when done. After acceptance, use New attempt for a separate attempt; the server enforces the configured attempt limit.
          </Typography>

          {loadingSelectors ? (
            <CircularProgress size={24} aria-label="Loading assessment and event choices" />
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="batch-instance-label">Assessment Instance</InputLabel>
                <Select
                  labelId="batch-instance-label"
                  label="Assessment Instance"
                  value={selectedInstanceId}
                  onChange={(e) => {
                    rosterRequestIdRef.current++;
                    setSelectedInstanceId(e.target.value);
                    setRows([]);
                    setEvent(null);
                    setRosterOwnerKey(null);
                    setLoadingRoster(false);
                  }}
                >
                  {instances.map((inst) => (
                    <MenuItem key={inst.id} value={inst.id}>
                      {inst.name ?? inst.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="batch-event-label">Event</InputLabel>
                <Select
                  labelId="batch-event-label"
                  label="Event"
                  value={selectedEventId}
                  onChange={(e) => {
                    rosterRequestIdRef.current++;
                    setSelectedEventId(e.target.value);
                    setRows([]);
                    setEvent(null);
                    setRosterOwnerKey(null);
                    setLoadingRoster(false);
                  }}
                >
                  {eventItems.map((ev) => (
                    <MenuItem key={ev.id} value={ev.id}>
                      {ev.name}
                      {ev.scoring_mode === 'pass_fail' && (
                        <Chip label="GO/NO-GO" size="small" sx={{ ml: 1 }} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={loadRoster}
                disabled={!selectedInstanceId || !selectedEventId || !activeCaptureSession || loadingRoster}
              >
                Load Roster
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {loadingRoster && <LinearProgress aria-label="Loading roster" />}

      {rows.length > 0 && event && rosterOwnedByActiveSession && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {event.name}
              </Typography>
              <Chip
                label={event.scoring_mode === 'pass_fail' ? 'GO / NO-GO' : schemeId}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip label={`${rows.length} trainees`} size="small" />
              {elapsedSec && (
                <Chip
                  label={`Completed in ${elapsedSec}s`}
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon />}
                />
              )}
            </Box>

            {event.scoring_mode === 'pass_fail' && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Click <strong>GO</strong> or <strong>NO-GO</strong> for each trainee.
                Rows with no selection are skipped.
              </Alert>
            )}

            <TableContainer ref={rosterRef} role="region" aria-label="Scrollable capture roster" tabIndex={0}>
              <Table aria-label={`${event.name} capture roster`} size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Trainee</TableCell>
                    <TableCell sx={{ minWidth: 240 }}>
                      {event.scoring_mode === 'pass_fail'
                        ? 'Result'
                        : `Result (${resolveLabels(schemeId).join(' / ')})`}
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={row.student.id}
                      data-capture-row={idx}
                      sx={{
                        bgcolor:
                          row.status === 'saved'
                            ? 'success.50'
                            : row.status === 'error'
                            ? 'error.50'
                            : 'inherit',
                      }}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell component="th" scope="row" id={`trainee-${idx}`}>
                        <Typography variant="body2">
                          {row.student.last_name}, {row.student.first_name}
                          {row.student.middle_initial ? ` ${row.student.middle_initial}.` : ''}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {event.scoring_mode === 'pass_fail' ? (
                          <Box>
                          {boundMeasures[0] && <TextField select label={`${row.student.last_name}, ${row.student.first_name} — observation state`} value={row.captured?.[boundMeasures[0].id]?.status ?? 'observed'} disabled={boundTeam || submitting || !!row.client_op_id}
                            onChange={e => setRows(prev => prev.map((r, ri) => ri === idx ? { ...r, captured: { [boundMeasures[0].id]: { status: e.target.value as CapturedMeasurement['status'], value: '' } } } : r))}>
                            {['observed', 'missing', 'not_attempted', 'invalid'].map(status => <MenuItem key={status} value={status}>{status.replaceAll('_', ' ')}</MenuItem>)}
                          </TextField>}
                          <ToggleButtonGroup
                            aria-labelledby={`trainee-${idx}`}
                            aria-describedby={`capture-status-${idx}`}
                            exclusive
                            size="small"
                            value={row.verdict ?? null}
                            onChange={(_, val) => {
                              if (!val) return;
                              setRows((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], verdict: val, status: 'idle', client_op_id: undefined };
                                return next;
                              });
                            }}
                            disabled={(binding ? !configuredEvent || boundTeam : instanceConfigured) || submitting || !!row.client_op_id || (boundMeasures[0] && (row.captured?.[boundMeasures[0].id]?.status ?? 'observed') !== 'observed')}
                          >
                            <ToggleButton
                              value="GO"
                              color="success"
                              sx={{ px: 2, fontWeight: 'bold' }}
                            >
                              GO
                            </ToggleButton>
                            <ToggleButton
                              value="NO-GO"
                              color="error"
                              sx={{ px: 2, fontWeight: 'bold' }}
                            >
                              NO-GO
                            </ToggleButton>
                          </ToggleButtonGroup>
                          </Box>
                        ) : binding ? <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {boundMeasures.map((m, mi) => <Box key={m.id}>
                            <TextField select label={`${row.student.last_name}, ${row.student.first_name} — ${m.label} state`}
                              value={row.captured?.[m.id]?.status ?? 'observed'} disabled={boundTeam || submitting || !!row.client_op_id}
                              onChange={e => setRows(prev => prev.map((r, ri) => ri === idx ? { ...r, captured: { ...r.captured, [m.id]: { status: e.target.value as CapturedMeasurement['status'], value: r.captured?.[m.id]?.value ?? '' } } } : r))}>
                              {['observed', 'missing', 'not_attempted', 'invalid'].map(status => <MenuItem key={status} value={status}>{status.replaceAll('_', ' ')}</MenuItem>)}
                            </TextField>
                            <TextField type="number" label={`${row.student.last_name}, ${row.student.first_name} — ${m.label} (${m.unit})`}
                              value={row.captured?.[m.id]?.value ?? ''} disabled={boundTeam || submitting || !!row.client_op_id || (row.captured?.[m.id]?.status ?? 'observed') !== 'observed'}
                              inputRef={element => { if (element) inputRefs.current.set(`${idx}-${mi}`, element); else inputRefs.current.delete(`${idx}-${mi}`); }}
                              inputProps={{ 'aria-describedby': `capture-status-${idx}` }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); advanceFrom(idx); } }}
                              onChange={e => setRows(prev => prev.map((r, ri) => ri === idx ? { ...r, captured: { ...r.captured, [m.id]: { status: 'observed', value: e.target.value } } } : r))} />
                          </Box>)}
                        </Box> : (
                          <MeasInput
                            schemeId={schemeId}
                            accessibleName={`${row.student.last_name}, ${row.student.first_name} — ${event.measurements?.[0]?.name ?? event.name}`}
                            statusId={`capture-status-${idx}`}
                            inputs={row.inputs}
                            disabled={!configuredEvent || boundTeam || submitting || !!row.client_op_id}
                            rowIdx={idx}
                            inputRefs={inputRefs}
                            onChange={(inputs) => {
                              setRows((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], inputs, status: 'idle', client_op_id: undefined };
                                return next;
                              });
                            }}
                            onAdvance={() => advanceFrom(idx)}
                          />
                        )}
                      </TableCell>

                      <TableCell id={`capture-status-${idx}`}>
                        {row.client_op_id && pendingByOpId.get(row.client_op_id)?.payload && <Typography variant="caption">{(pendingByOpId.get(row.client_op_id)!.payload as QueuedAttempt).capture_schema_version === 2 ? 'Configuration-bound schema 2' : 'Historical schema 1 — original payload preserved'}</Typography>}
                        {(() => {
                          // Prefer queue-derived state when available.
                          const q = row.client_op_id ? pendingByOpId.get(row.client_op_id) : undefined;
                          if (q?.status === 'syncing') {
                            return (
                              <Box><SyncIcon fontSize="small" color="info" /> Syncing to server</Box>
                            );
                          }
                          if (q?.status === 'queued') {
                            return (
                              <Box><CloudQueueIcon fontSize="small" color={isOnline ? 'action' : 'warning'} /> Saved locally; pending server acceptance</Box>
                            );
                          }
                          if (row.status === 'saving') return <Box><CircularProgress size={16} aria-label="Saving attempt locally" /> Saving locally</Box>;
                          if (row.status === 'saved') {
                            const acceptedHere = (pending as QueueItem<QueuedAttempt>[]).filter(item =>
                              item.status === 'synced' && item.payload.student_id === row.student.id).length;
                            const reachedKnownLimit = !!event.max_attempts && acceptedHere >= event.max_attempts;
                            return <Box>
                              <Box><CheckCircleIcon fontSize="small" color="success" /> Accepted by server</Box>
                              <Button size="small" disabled={!configuredEvent || boundTeam || reachedKnownLimit} onClick={() => { focusAfterRender.current = idx; setRows(previous => previous.map((current, index) =>
                                index === idx ? { ...current, inputs: Array(partCount(schemeId)).fill(''), verdict: undefined,
                                  status: 'idle', captured: {}, client_op_id: undefined, errorMsg: undefined } : current)); }}>
                                New attempt
                              </Button>
                              {reachedKnownLimit && <Typography variant="caption">Attempt limit reached</Typography>}
                            </Box>;
                          }
                          if (row.status === 'error') {
                            return (
                              <Box><ErrorIcon fontSize="small" color="error" /> {row.errorMsg ?? 'Not accepted; review required'}</Box>
                            );
                          }
                          return <Typography variant="body2">Not saved</Typography>;
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                ref={saveButtonRef}
                onClick={handleSubmit}
                disabled={(binding ? !configuredEvent || boundTeam : instanceConfigured) || submitting || !activeCaptureSession || rows.every((r) => r.status === 'saved')}
              >
                {submitting
                  ? `Saving… ${savedCount}/${rows.length}`
                  : `Save All (${rows.length} trainees)`}
              </Button>
              {submitting && (
                <LinearProgress
                  aria-label="Server acceptance progress"
                  variant="determinate"
                  value={(savedCount / rows.length) * 100}
                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                />
              )}
              {!submitting && savedCount > 0 && (
                <Typography variant="body2" color="text.primary">
                  {savedCount} accepted by server · {elapsedSec}s
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
